import { GraphQlRequestService } from '../../../api/graphql-request-service';
import { Domains } from '../../constants';
import { Hasher } from '../hasher';
import { withMappedCreatorPrincipal } from '../principal/utils';
import {
  DomainSettings,
  HasDependencies,
  hashString,
  LatestDomainSettings,
  Role,
} from '../types';
import { handleGQLErrors } from '../utils';
import { DomainSettingsFetchError, DomainSettingsUpdateError } from './errors';

export interface DomainSettingsService extends HasDependencies {
  getLatestWithVersion: (domain: Domains) => Promise<LatestDomainSettings>;
  getLatest: (domain: Domains) => Promise<DomainSettings>;
  update: (
    domain: Domains,
    preferences: DomainSettings
  ) => Promise<DomainSettings>;
}

export class BackendDomainSettingsService implements DomainSettingsService {
  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly simulateRole: Role | null
  ) {}

  public getDeps = () => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      Hasher.hash(this.simulateRole),
    ]),
  ];

  public getLatestWithVersion = async (
    domain: Domains
  ): Promise<LatestDomainSettings> => {
    const response = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.domainSettingsLatestVersion({
        domain,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
    const result = response.domainSettingsLatestVersion;
    if (!result) {
      throw new DomainSettingsFetchError(domain, new Error('response is null'));
    }
    return withMappedCreatorPrincipal(result);
  };

  public getLatest = async (domain: Domains): Promise<DomainSettings> => {
    const response = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.domainSettingsLatestVersion({
        domain,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
    const result = response.domainSettingsLatestVersion;
    if (!result) {
      throw new DomainSettingsFetchError(domain, new Error('response is null'));
    }
    return result.settings as DomainSettings;
  };

  public update = async (
    domain: Domains,
    settings: DomainSettings
  ): Promise<DomainSettings> => {
    const response = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.createNewDomainSettings({
        domain,
        settings,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
    const result = response.createNewDomainSettings;
    if (!result) {
      throw new DomainSettingsUpdateError(
        domain,
        new Error('response is null')
      );
    }
    return result.settings as DomainSettings;
  };
}
